/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import GAEvent from './analytics';
import isNonProduction from './isNonProduction';

/**
 * Payment-flow telemetry.
 *
 * Users are intermittently bounced from the Payment page (credit-card iframe)
 * back to the start of the flow. This module records enough context on every
 * redirect decision to explain *why* it happened, and keeps a small per-tab
 * marker so a redirect that follows a genuine visit to the payment form can be
 * told apart from a first visit.
 *
 * Everything here is best-effort: storage or Sentry failures never affect the
 * user-facing flow.
 */

export type FlowPage = 'Summary' | 'Billing' | 'Payment' | 'Confirmation';

export type FlowRedirectReason =
  /** Payment page rendered without paymentFormInfo in memory (Base.tsx). */
  | 'payment_form_info_missing'
  /** Requested page is not in allowedPages for the current order status (Base.tsx). */
  | 'page_not_allowed_for_status'
  /** Order is cancelled or expired without a grace period (Base.tsx). */
  | 'order_cancelled_or_expired'
  /** Order is completed/paid so the user is forwarded to Confirmation (Base.tsx). */
  | 'order_complete_go_to_confirmation'
  /** Client-side billing address check failed after a card token arrived. */
  | 'client_billing_validation'
  /** Pay API reported an address / ZIP (AVS) failure, user sent back to Billing. */
  | 'avs_address_or_zip_failure'
  /** PayPal capture failed and the user chose to go back to Billing. */
  | 'paypal_error_back_to_billing';

/**
 * Sentry level used when a redirect is captured as an event. `null` means the
 * redirect is a normal part of the flow and is recorded as a breadcrumb only.
 */
const CAPTURE_LEVEL: Record<FlowRedirectReason, Sentry.SeverityLevel | null> = {
  payment_form_info_missing: 'warning',
  page_not_allowed_for_status: 'warning',
  client_billing_validation: 'warning',
  avs_address_or_zip_failure: 'info',
  paypal_error_back_to_billing: 'info',
  order_cancelled_or_expired: null,
  order_complete_go_to_confirmation: null,
};

export interface FlowRedirectContext {
  invoiceId: string;
  fromPage: FlowPage;
  toPath: string;
  orderId?: number;
  tenantId?: number;
  orderStatus?: string;
  allowedPages?: FlowPage[];
  hadPaymentFormInfo?: boolean;
  paymentType?: string;
  /** Short, PII-free detail such as the failing billing fields. */
  detail?: string;
}

/** Per-tab record of how far the user got in the payment flow. */
export interface PaymentFlowMarker {
  invoiceId: string;
  paymentType: string;
  reachedPaymentAt: number;
  paymentPageVisits: number;
  iframeLoads: number;
  iframeEvents: number;
  payAttempts: number;
  lastIframeEvent?: 'success' | 'failure';
  lastIframeFailureCode?: string;
  lastIframeEventAt?: number;
  /** GUID from the pay-form URL; joins to the gateway's CloudWatch logs. */
  hostedTransactionId?: string;
}

export const FLOW_MARKER_KEY = 'invoicePortal.paymentFlow';
const BREADCRUMB_CATEGORY = 'payment-flow';

// sessionStorage is per tab and survives reloads and mobile tab discard/restore,
// which is exactly the boundary we need to see across. Fall back to memory if
// it is unavailable (private mode, blocked storage).
let memoryMarker: PaymentFlowMarker | null = null;

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const probe = '__flow_probe__';
    window.sessionStorage.setItem(probe, probe);
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

export function readFlowMarker(): PaymentFlowMarker | null {
  const store = storage();
  if (!store) return memoryMarker;
  try {
    const raw = store.getItem(FLOW_MARKER_KEY);
    return raw ? (JSON.parse(raw) as PaymentFlowMarker) : null;
  } catch (e) {
    return null;
  }
}

function writeFlowMarker(marker: PaymentFlowMarker): void {
  const store = storage();
  if (!store) {
    memoryMarker = marker;
    return;
  }
  try {
    store.setItem(FLOW_MARKER_KEY, JSON.stringify(marker));
  } catch (e) {
    memoryMarker = marker;
  }
}

export function clearFlowMarker(): void {
  memoryMarker = null;
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(FLOW_MARKER_KEY);
  } catch (e) {
    // ignore
  }
}

function updateFlowMarker(
  update: (marker: PaymentFlowMarker) => PaymentFlowMarker
): void {
  const current = readFlowMarker();
  if (!current) return;
  writeFlowMarker(update(current));
}

export type NavigationType =
  | 'navigate'
  | 'reload'
  | 'back_forward'
  | 'prerender'
  | 'unknown';

/** How the current document was reached (fresh navigation, reload, history). */
export function getNavigationType(): NavigationType {
  try {
    const entries = performance.getEntriesByType?.('navigation') as
      | PerformanceNavigationTiming[]
      | undefined;
    const type = entries?.[0]?.type;
    if (type) return type as NavigationType;
  } catch (e) {
    // fall through to the legacy API
  }
  try {
    // Deprecated but still the only signal on some older mobile browsers.
    const legacy = performance.navigation?.type;
    if (legacy === 1) return 'reload';
    if (legacy === 2) return 'back_forward';
    if (legacy === 0) return 'navigate';
  } catch (e) {
    // ignore
  }
  return 'unknown';
}

/**
 * True when the browser discarded this tab (memory pressure, typically mobile)
 * and reloaded it when the user came back. Chromium only; undefined elsewhere.
 */
export function wasDocumentDiscarded(): boolean {
  return (
    (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true
  );
}

export function addFlowBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void {
  try {
    Sentry.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message,
      data,
      level,
    });
    if (isNonProduction) {
      console.info(`[payment-flow] ${message}`, data ?? '');
    }
  } catch (e) {
    // never let telemetry break the flow
  }
}

/** Tag every subsequent Sentry event in this session with the invoice/order. */
export function setFlowScope(scope: {
  invoiceId: string;
  orderId?: number;
  tenantId?: number;
}): void {
  try {
    Sentry.setTag('invoice.reference_key', scope.invoiceId);
    if (scope.orderId !== undefined) {
      Sentry.setTag('order.id', String(scope.orderId));
    }
    if (scope.tenantId !== undefined) {
      Sentry.setTag('tenant.id', String(scope.tenantId));
    }
  } catch (e) {
    // ignore
  }
}

/** Call when the Payment page (with a payment form) actually renders. */
export function markPaymentPageReached(
  invoiceId: string,
  paymentType: string
): void {
  const existing = readFlowMarker();
  const sameInvoice = !!existing && existing.invoiceId === invoiceId;
  if (existing && sameInvoice) {
    writeFlowMarker({
      ...existing,
      paymentType,
      paymentPageVisits: existing.paymentPageVisits + 1,
    });
  } else {
    writeFlowMarker({
      invoiceId,
      paymentType,
      reachedPaymentAt: Date.now(),
      paymentPageVisits: 1,
      iframeLoads: 0,
      iframeEvents: 0,
      payAttempts: 0,
    });
  }
  addFlowBreadcrumb('payment page rendered', {
    invoiceId,
    paymentType,
    visits: (sameInvoice && existing ? existing.paymentPageVisits : 0) + 1,
  });
}

export function recordIframeLoad(iframeKey: number): void {
  updateFlowMarker((m) => ({ ...m, iframeLoads: m.iframeLoads + 1 }));
  addFlowBreadcrumb('payment iframe loaded', { iframeKey });
}

export function recordIframeEvent(
  eventType: 'success' | 'failure',
  code?: string,
  reason?: string
): void {
  updateFlowMarker((m) => ({
    ...m,
    iframeEvents: m.iframeEvents + 1,
    lastIframeEvent: eventType,
    lastIframeFailureCode: eventType === 'failure' ? code : undefined,
    lastIframeEventAt: Date.now(),
  }));
  addFlowBreadcrumb(
    `payment iframe ${eventType}`,
    eventType === 'failure' ? { code, reason } : undefined,
    eventType === 'failure' ? 'warning' : 'info'
  );
}

export function recordPayAttempt(): void {
  updateFlowMarker((m) => ({ ...m, payAttempts: m.payAttempts + 1 }));
}

/**
 * A payment form stored in this tab was restored after a fresh document load
 * (reload, back/forward, tab discard). On the Payment page this is a bounce
 * that the persisted form prevented, so it is captured as an info event; its
 * volume should mirror the drop in `page_not_allowed_for_status`.
 */
export function trackPaymentFormRestored(ctx: {
  invoiceId: string;
  orderId?: number;
  page: FlowPage;
  paymentType: string;
  ageSeconds: number;
  orderStatus?: string;
}): void {
  addFlowBreadcrumb('payment form restored from session storage', {
    page: ctx.page,
    ageSeconds: ctx.ageSeconds,
    orderStatus: ctx.orderStatus,
  });
  if (ctx.page !== 'Payment') return;
  try {
    const navigationType = getNavigationType();
    Sentry.captureMessage('Payment form restored after page load', {
      level: 'info',
      tags: {
        'flow.from_page': ctx.page,
        'flow.payment_type': ctx.paymentType,
        'flow.navigation_type': navigationType,
        'flow.document_discarded': String(wasDocumentDiscarded()),
        'invoice.reference_key': ctx.invoiceId,
        ...(ctx.orderId !== undefined && { 'order.id': String(ctx.orderId) }),
        ...(ctx.orderStatus && {
          'order.status': ctx.orderStatus.toLowerCase(),
        }),
      },
      extra: { ageSeconds: ctx.ageSeconds },
    });
  } catch (e) {
    // ignore
  }
}

/**
 * The Payment page loaded for an order that was finalized earlier, but with no
 * payment form in memory or in this tab's storage (a browser tab restore, a
 * history entry, an expired stored form). Base called finalize again for a
 * fresh form instead of bouncing the user to Billing. On success this is a
 * bounce prevented; on failure the user is sent to Billing as before.
 */
export function trackPaymentFormRefinalized(ctx: {
  invoiceId: string;
  orderId?: number;
  paymentType: string;
  outcome: 'success' | 'failure';
  storedPaymentForm: string;
  statusCode?: number;
}): void {
  addFlowBreadcrumb(
    `payment form re-finalized on load: ${ctx.outcome}`,
    { storedPaymentForm: ctx.storedPaymentForm, statusCode: ctx.statusCode },
    ctx.outcome === 'success' ? 'info' : 'warning'
  );
  try {
    Sentry.captureMessage(`Payment form re-finalized on page load: ${ctx.outcome}`, {
      level: ctx.outcome === 'success' ? 'info' : 'warning',
      tags: {
        'flow.from_page': 'Payment',
        'flow.payment_type': ctx.paymentType,
        'flow.navigation_type': getNavigationType(),
        'flow.reached_payment_in_tab': String(
          readFlowMarker()?.invoiceId === ctx.invoiceId
        ),
        'flow.stored_payment_form': ctx.storedPaymentForm,
        'invoice.reference_key': ctx.invoiceId,
        ...(ctx.orderId !== undefined && { 'order.id': String(ctx.orderId) }),
        ...(ctx.statusCode !== undefined && {
          'api.status': String(ctx.statusCode),
        }),
      },
    });
  } catch (e) {
    // ignore
  }
}

/**
 * The pay API can answer `isSuccess: false` with a failures object whose four
 * fields are all null (plain decline / capture failure). The UI then shows no
 * message and simply reloads the card form, which users read as "it reset on
 * me". Capture it so we can size the problem.
 */
export function trackPaymentFailureWithoutDetails(ctx: {
  invoiceId: string;
  orderId?: number;
}): void {
  const marker = readFlowMarker();
  addFlowBreadcrumb('pay: failed with no failure details', ctx, 'warning');
  try {
    Sentry.captureMessage('Payment failed with no failure details shown to user', {
      level: 'warning',
      tags: {
        'flow.from_page': 'Payment',
        'invoice.reference_key': ctx.invoiceId,
        ...(ctx.orderId !== undefined && { 'order.id': String(ctx.orderId) }),
        ...(marker?.hostedTransactionId && {
          'gateway.hosted_transaction_id': marker.hostedTransactionId,
        }),
      },
      extra: { payAttempts: marker?.payAttempts },
    });
  } catch (e) {
    // ignore
  }
}

/**
 * PaymentIframe redraws the gateway form after each failure but gives up after
 * a fixed number of attempts, leaving the user with a dead form. Capture it.
 */
export function trackIframeRetryLimitReached(ctx: {
  iframeKey: number;
  code?: string;
  reason?: string;
}): void {
  const marker = readFlowMarker();
  addFlowBreadcrumb('payment iframe retry limit reached', ctx, 'warning');
  try {
    Sentry.captureMessage('Payment iframe retry limit reached', {
      level: 'warning',
      tags: {
        'flow.from_page': 'Payment',
        'gateway.failure_code': ctx.code ?? 'none',
        ...(marker?.invoiceId && { 'invoice.reference_key': marker.invoiceId }),
        ...(marker?.hostedTransactionId && {
          'gateway.hosted_transaction_id': marker.hostedTransactionId,
        }),
      },
      extra: { ...ctx, iframeEvents: marker?.iframeEvents },
    });
  } catch (e) {
    // ignore
  }
}

/**
 * The gateway logs every hosted-form request to CloudWatch keyed by
 * HostedTransactionId. Pulling it out of the form URL lets a front-end event be
 * joined to those server logs. It is an opaque GUID, not PII.
 */
export function getHostedTransactionId(payFormUrl: string): string | undefined {
  try {
    const params = new URL(payFormUrl).searchParams;
    const key = Array.from(params.keys()).find(
      (k) => k.toLowerCase() === 'hostedtransactionid'
    );
    return key ? params.get(key) ?? undefined : undefined;
  } catch (e) {
    return undefined;
  }
}

/** Remember which hosted transaction the current iframe is showing. */
export function recordPaymentFormUrl(payFormUrl: string): void {
  const hostedTransactionId = getHostedTransactionId(payFormUrl);
  updateFlowMarker((m) => ({ ...m, hostedTransactionId }));
  try {
    Sentry.setTag('gateway.hosted_transaction_id', hostedTransactionId ?? 'none');
  } catch (e) {
    // ignore
  }
}

/**
 * SWR refetched the invoice (focus / reconnect) and the request failed. Base
 * then stops rendering the flow pages, which unmounts the payment iframe and
 * wipes whatever the user had typed. Worth knowing how often that happens.
 */
export function trackInvoiceRefetchFailure(ctx: {
  invoiceId: string;
  page: FlowPage;
  statusCode?: number;
  message?: string;
}): void {
  addFlowBreadcrumb(
    'invoice refetch failed',
    { page: ctx.page, statusCode: ctx.statusCode },
    'warning'
  );
  if (ctx.page !== 'Payment') return;
  try {
    Sentry.captureMessage('Invoice refetch failed while on Payment page', {
      level: 'warning',
      tags: {
        'flow.from_page': ctx.page,
        'invoice.reference_key': ctx.invoiceId,
        'flow.navigation_type': getNavigationType(),
      },
      extra: {
        statusCode: ctx.statusCode,
        message: ctx.message,
        visibilityState:
          typeof document !== 'undefined' ? document.visibilityState : undefined,
      },
    });
  } catch (e) {
    // ignore
  }
}

/**
 * Record a redirect decision. Always leaves a breadcrumb; for reasons that
 * indicate a user was knocked out of the flow it also raises a Sentry event
 * (grouped per reason) and a GA event so support can see counts per tenant.
 */
export function trackFlowRedirect(
  reason: FlowRedirectReason,
  ctx: FlowRedirectContext
): void {
  try {
    const marker = readFlowMarker();
    const reachedPaymentInTab = !!marker && marker.invoiceId === ctx.invoiceId;
    const level = CAPTURE_LEVEL[reason];
    const navigationType = getNavigationType();
    const discarded = wasDocumentDiscarded();

    const tags: Record<string, string> = {
      'flow.redirect_reason': reason,
      'flow.from_page': ctx.fromPage,
      'flow.reached_payment_in_tab': String(reachedPaymentInTab),
      'flow.navigation_type': navigationType,
      'flow.document_discarded': String(discarded),
      'flow.payment_type': ctx.paymentType ?? marker?.paymentType ?? 'unknown',
      'invoice.reference_key': ctx.invoiceId,
    };
    if (ctx.orderId !== undefined) tags['order.id'] = String(ctx.orderId);
    if (ctx.orderStatus) tags['order.status'] = ctx.orderStatus;
    if (ctx.tenantId !== undefined) tags['tenant.id'] = String(ctx.tenantId);

    const reached = reachedPaymentInTab ? (marker as PaymentFlowMarker) : null;
    const extra: Record<string, unknown> = {
      toPath: ctx.toPath,
      allowedPages: ctx.allowedPages,
      hadPaymentFormInfo: ctx.hadPaymentFormInfo,
      detail: ctx.detail,
      visibilityState:
        typeof document !== 'undefined' ? document.visibilityState : undefined,
      sessionStorageAvailable: storage() !== null,
      secondsSinceReachedPayment: reached
        ? Math.round((Date.now() - reached.reachedPaymentAt) / 1000)
        : null,
      paymentPageVisits: reached?.paymentPageVisits ?? 0,
      iframeLoads: reached?.iframeLoads ?? 0,
      iframeEvents: reached?.iframeEvents ?? 0,
      payAttempts: reached?.payAttempts ?? 0,
      lastIframeEvent: reached?.lastIframeEvent,
      lastIframeFailureCode: reached?.lastIframeFailureCode,
      hostedTransactionId: reached?.hostedTransactionId,
    };
    if (reached?.hostedTransactionId) {
      tags['gateway.hosted_transaction_id'] = reached.hostedTransactionId;
    }

    addFlowBreadcrumb(
      `redirect: ${reason} (${ctx.fromPage} -> ${ctx.toPath})`,
      { ...tags, ...extra },
      level ?? 'info'
    );

    if (level) {
      Sentry.captureMessage(`Payment flow redirect: ${reason}`, {
        level,
        tags,
        extra,
      });
      if (ctx.tenantId !== undefined) {
        GAEvent(`Flow Redirect - ${reason}`, ctx.tenantId);
      }
    }
  } catch (e) {
    console.error('[payment-flow] failed to record redirect', e);
  }
}

let lifecycleInstalled = false;

/**
 * One-time boot telemetry: tags the session with how the document was reached
 * and records tab visibility / page lifecycle transitions as breadcrumbs so a
 * later redirect event shows whether the user left the tab before it happened.
 */
export function installPageLifecycleTelemetry(): void {
  if (lifecycleInstalled || typeof window === 'undefined') return;
  lifecycleInstalled = true;
  try {
    const navigationType = getNavigationType();
    const discarded = wasDocumentDiscarded();
    Sentry.setTag('page.navigation_type', navigationType);
    Sentry.setTag('page.was_discarded', String(discarded));
    addFlowBreadcrumb('document loaded', {
      navigationType,
      wasDiscarded: discarded,
      visibilityState: document.visibilityState,
      hasFlowMarker: readFlowMarker() !== null,
      path: window.location.pathname,
    });

    document.addEventListener('visibilitychange', () => {
      addFlowBreadcrumb(`visibility: ${document.visibilityState}`);
    });
    window.addEventListener('pagehide', (event: PageTransitionEvent) => {
      addFlowBreadcrumb('pagehide', { persisted: event.persisted });
    });
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      addFlowBreadcrumb('pageshow', { persisted: event.persisted });
    });
  } catch (e) {
    // ignore
  }
}

/** Test-only: reset module state between tests. */
export function resetPaymentFlowTelemetryForTests(): void {
  lifecycleInstalled = false;
  memoryMarker = null;
}
